import React from "react";

import _ from "lodash";

import useAutocomplete, {
    AutocompleteHookType,
    ElementType,
    GetItemsInterface,
    SetItemInterface,
} from "commons/hooks/useAutocomplete";
import useDragAndDropList, { DragAndDropListHook } from "commons/hooks/useDragAndDropList";
import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionHighlightApi from "hooks/api/private/blocks/useSectionHighlightApi";
import useSectionHighlightProductApi from "hooks/api/private/blocks/useSectionHighlightProductApi";
import useProductApi from "hooks/api/private/useProductApi";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionGalleryBody } from "types/api/blocks/sectionGallery";

import { createAdvancedListRequestFromFilter, filterEq, filterLike } from "utils/utils";

type ProductForm = {
    section_highlight_product_id?: string;
    section_highlight_id: string;
    product_id: string;
    product_name: string;
    graphical_order: number;
    toDelete: boolean;
    toCreate: boolean;
    toUpdate: boolean;
};

type HighlightProductsForm = {
    products: Array<ProductForm>;
};

type SectionProductHighlightType = {
    buttonDelete: ButtonDeleteInterface;
    onDeleteIconClick: (productId: string) => void;
    productAutocompleteHook: AutocompleteHookType<string>;
    dragAndDropHook: DragAndDropListHook<unknown>;
    formHook: FormHookType<HighlightProductsForm>;
};

function useSectionProductHighlight(): SectionProductHighlightType {
    const { getAllProduct } = useProductApi();

    const { saveSectionHighlight, deleteSectionHighlight } = useSectionHighlightApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const {
        createSectionHighlightProduct,
        updateSectionHighlightProduct,
        deleteSectionHighlightProduct,
        getAllSectionHighlightProduct,
    } = useSectionHighlightProductApi();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                products: {
                    type: "objectArray",
                    of: {
                        type: "object",
                        properties: {},
                    },
                },
            },
        };
    }, []);

    const initialValues: HighlightProductsForm = React.useMemo(() => {
        return {
            products: [],
        };
    }, []);

    const formHook = useForm<HighlightProductsForm>(schema, initialValues);

    const { setForm, showAllError, form } = formHook;

    const getProducts: GetItemsInterface<string> = React.useCallback(
        (setItems: SetItemInterface<string>, value: string) => {
            const [apiCall, abortController] = getAllProduct(
                createAdvancedListRequestFromFilter(filterLike("products.name", value)),
            );

            apiCall.then((response) => {
                setItems(() => {
                    const finalItems: ElementType<string>[] = [];

                    response.content.forEach((product) => {
                        finalItems.push({
                            value: product.id,
                            label: `${product.name} | ${product.category_name}`,
                        });
                    });

                    return finalItems;
                });
            });

            return abortController;
        },
        [getAllProduct],
    );

    const onProductsValueChange = React.useCallback(
        (element: ElementType<string> | undefined) => {
            if (element !== undefined) {
                setForm((prevForm) => {
                    const newForm = _.cloneDeep(prevForm);

                    // to avoid adding the same element twice
                    const elementIsNotPresent: boolean = newForm.products.every(
                        (product) => product.product_id !== element.value,
                    );

                    // check if the lenght of the shown product is less than 4
                    const lowerThanFourElements: () => boolean = () => {
                        const finalValue = newForm.products.filter((product) => {
                            if (!product.toDelete) {
                                return product;
                            }

                            return null;
                        });

                        return finalValue.length < 4;
                    };

                    if (elementIsNotPresent && lowerThanFourElements()) {
                        newForm.products.push({
                            section_highlight_id: blockGroupCompositionId,
                            product_id: element.value,
                            graphical_order: (newForm.products.length + 1) * 10,
                            product_name: element.label,
                            toDelete: false,
                            toCreate: true,
                            toUpdate: false,
                        });
                    }
                    return newForm;
                });
            }
        },
        [setForm, blockGroupCompositionId],
    );

    const productAutocompleteHook = useAutocomplete(getProducts, onProductsValueChange, "", true);

    const onDeleteIconClick = React.useCallback(
        (productId: string) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const finalProducts = newForm.products.map((product) => {
                    if (product.product_id === productId) {
                        return { ...product, toDelete: true };
                    }

                    return product;
                });

                const finalForm: HighlightProductsForm = { products: finalProducts };

                return finalForm;
            });
        },
        [setForm],
    );

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            return new Promise<void>((resolve, reject) => {
                const body: SectionGalleryBody = {
                    id: blockGroupCompositionId,
                };

                const [apiCall] = saveSectionHighlight(body);

                apiCall
                    .then(() => {
                        const promises: Promise<unknown>[] = [];

                        form.products.forEach((product) => {
                            if (product.toCreate === true) {
                                const [apiCall] = createSectionHighlightProduct({
                                    section_highlight_id: blockGroupCompositionId,
                                    product_id: product.product_id,
                                    graphical_order: product.graphical_order,
                                });

                                promises.push(apiCall);

                                apiCall.then((response) => {
                                    setForm((prevForm) => {
                                        const newForm = _.cloneDeep(prevForm);

                                        const index = newForm.products.findIndex(
                                            (item) => item.product_id === product.product_id,
                                        );

                                        if (index !== -1) {
                                            newForm.products[index].section_highlight_product_id =
                                                response.id;
                                            newForm.products[index].toCreate = false;
                                        }

                                        return newForm;
                                    });
                                });
                            }

                            if (product.toDelete === true && product.section_highlight_product_id) {
                                const [apiCall] = deleteSectionHighlightProduct({
                                    id: product.section_highlight_product_id,
                                });

                                promises.push(apiCall);

                                apiCall.then(() => {
                                    setForm((prevForm) => {
                                        const newForm = _.cloneDeep(prevForm);

                                        const index = newForm.products.findIndex(
                                            (item) => item.product_id === product.product_id,
                                        );

                                        if (index !== -1) {
                                            newForm.products.splice(index, 1);
                                        }

                                        return newForm;
                                    });
                                });
                            }

                            if (product.toUpdate === true && product.section_highlight_product_id) {
                                const [apiCall] = updateSectionHighlightProduct(
                                    { id: product.section_highlight_product_id },
                                    {
                                        section_highlight_id: blockGroupCompositionId,
                                        product_id: product.product_id,
                                        graphical_order: product.graphical_order,
                                    },
                                );

                                promises.push(apiCall);
                            }
                        });

                        Promise.all(promises)
                            .then(() => resolve())
                            .catch((reason) => reject(reason));
                    })
                    .catch((reason) => reject(reason));
            });
        });
    }, [
        createSectionHighlightProduct,
        deleteSectionHighlightProduct,
        updateSectionHighlightProduct,
        saveSectionHighlight,
        addSaveAction,
        showAllError,
        setForm,
        blockGroupCompositionId,
        blockGroupId,
        blockId,
        graphicalOrder,
        form,
    ]);

    React.useEffect(() => {
        const [apiCall] = getAllSectionHighlightProduct(
            createAdvancedListRequestFromFilter(
                filterEq(
                    "section_highlight_products.section_highlight_id",
                    blockGroupCompositionId,
                ),
                "section_highlight_products.graphical_order,ASC",
            ),
        );

        apiCall.then((res) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.products = res.content.map((product): ProductForm => {
                    return {
                        section_highlight_product_id: product.section_highlight_product_id,
                        section_highlight_id: product.section_highlight_id,
                        product_id: product.id,
                        product_name: `${product.name} | ${product.category_name}`,
                        graphical_order: product.section_highlight_product_graphical_order,
                        toDelete: false,
                        toCreate: false,
                        toUpdate: false,
                    };
                });

                return newForm;
            });
        });
    }, [getAllSectionHighlightProduct, setForm, blockGroupCompositionId]);

    const onUpdateList = React.useCallback(
        (newList) => {
            setForm((prevList) => {
                const tmpList = _.cloneDeep(prevList);

                tmpList.products = newList;

                tmpList.products.forEach((singleRow, index) => {
                    tmpList.products[index].graphical_order = (index + 1) * 10;
                    tmpList.products[index].toUpdate = true;
                });

                return tmpList;
            });
        },
        [setForm],
    );

    const dragAndDropHook = useDragAndDropList({ list: form.products, onUpdateList });

    return React.useMemo(
        (): SectionProductHighlightType => ({
            buttonDelete: deleteSectionHighlight,
            onDeleteIconClick,
            productAutocompleteHook,
            formHook,
            dragAndDropHook,
        }),
        [
            deleteSectionHighlight,
            onDeleteIconClick,
            productAutocompleteHook,
            formHook,
            dragAndDropHook,
        ],
    );
}

export default useSectionProductHighlight;
